import { FC, lazy, Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { WithChildren } from '../../_metronic/helpers'
import { MasterLayout } from '../../_metronic/layout/MasterLayout'
import { constraintConfig } from '../../constraintConfig'
import { selectAuth } from '../../features/authSlice'
import { useAppSelector } from '../../hooks'
import { DashboardWrapper } from '../pages/dashboard/DashboardWrapper'
import { allRoutes } from './All_Routes'
import { LoadingComponent } from '../../components/loadingComponent/LoadingComponent'

/**
 * Eager primary-nav / daily listing shells.
 * Lazy Suspense was painting a blank toolbar + spinner that looked like a header loader.
 * These mount immediately; API loading stays inside each page's table/card body.
 */
import AllInvoicesAdmin from '../pages/Invoices/AllInvoicesAdmin'
import UsersPageWrapper from '../pages/users/UsersPageWrapper'
import ExpenseItemWrapper from '../pages/expenseItems/ExpenseItemWrapper'
import DraftItemsPage from '../pages/expenseItems/DraftItemsPage'
import ExpenseSubmissionWrapper from '../pages/expenseItems/ExpenseSubmissionWrapper'
import TeamMemberExpenses from '../pages/expenseItems/TeamMemberExpenses'
import InvoicesWrapper from '../pages/Invoices/InvoicesWrapper'
import CardWrapper from '../pages/vendorAndCard/card/CardWrapper'
import ExpenseSearch from '../pages/expenseSearch/ExpenseSearch'
import ReporteesPage from '../pages/reportees/ReporteesPage'
import BudgetHistory from '../pages/budget/BudgetHistory'
import BudgetPage from '../pages/budget/BudgetPage'
import CategoryBudgetWrapper from '../pages/settings/categoryBudgeting/CategoryBudgetWrapper'
import MyReportPage from '../pages/reportAnalytics/myReport/MyReportPage'
import TeamReportPage from '../pages/reportAnalytics/teamReport/TeamReportPage'
import QueryReportPage from '../pages/reportAnalytics/queryReport/QueryReportPage'
import AnomaliesDetectionPage from '../pages/expenseAnomalies/AnomaliesDetectionPage'
import ImpersonateUserPage from '../pages/impersonate-user'
import HelpSupportPage from '../pages/settings/helpCenter/HelpSupportPage'
import ExpenseForAp from '../pages/expenseItems/expenseForAp/ExpenseForAp'

// Less-frequent / settings / detail routes — keep lazy
const ClientWrapper = lazy(() => import('../pages/clientAndProject/client/ClientWrapper'))
const ProjectsWrapper = lazy(() => import('../pages/clientAndProject/projects/ProjectsWrapper'))
const Categories = lazy(() => import('../pages/configCategories/categories/Categories'))
const ExpenseItemDetail = lazy(() => import('../pages/expenseItems/ExpenseItemDetail'))
const GoogleMapComponent = lazy(() => import('../pages/expenseItems/GoogleMap'))
const BuilderPageWrapper = lazy(() => import('../pages/layout-builder/BuilderPageWrapper'))
const MenuTestPage = lazy(() => import('../pages/MenuTestPage').then((m) => ({ default: m.MenuTestPage })))
const MileageWrapper = lazy(() => import('../pages/mileage/MileageWrapper'))
const OrganizationPageWrapper = lazy(() => import('../pages/organization/OrganizationPageWrapper'))
const OrgSettings = lazy(() => import('../pages/orgSettings/OrgSettings'))
const SurveyCampaignsPage = lazy(() => import('../pages/survey/SurveyCampaignsPage'))
const SurveyDashboardPage = lazy(() => import('../pages/survey/SurveyDashboardPage'))
const UserNotificationsAdminPage = lazy(() => import('../pages/userNotifications/UserNotificationsAdminPage'))
const Report = lazy(() => import('../pages/reportAnalytics/Report'))
const RolesPageWrapper = lazy(() => import('../pages/roles/RolesPageWrapper'))
const AutomationCCFeedsStatsEnhancements = lazy(() => import('../pages/settings/automation/AutomationCCFeedsStatsEnhancements'))
const BankCCFeeds = lazy(() => import('../pages/settings/automation/BankCCFeeds'))
const BankCCFeedFiles = lazy(() => import('../pages/settings/automation/BankCCFeedFiles'))
const BankCCFeedMissingFiles = lazy(() => import('../pages/settings/automation/BankCCFeedMissingFiles'))
const SSToBCSyncFeeds = lazy(() => import('../pages/settings/automation/SSToBCSyncFeeds'))
const CountriesWrapper = lazy(() => import('../pages/settings/countries/CountriesWrapper'))
const DepartmentWrapper = lazy(() => import('../pages/settings/department/DepartmentWrapper'))
const DesignationWrapper = lazy(() => import('../pages/settings/designation/DesignationWrapper'))
const DivisionWrapper = lazy(() => import('../pages/settings/division/DivisionWrapper'))
const ExpenseVoilationWrapper = lazy(() => import('../pages/settings/expenseVoilation/ExpenseVoilationWrapper'))
const LocationWrapper = lazy(() => import('../pages/settings/locations/LocationWrapper'))
const VendorWrapper = lazy(() => import('../pages/vendorAndCard/vendor/VendorWrapper'))
const ApproversPage = lazy(() => import('../pages/workFlow/ApproversPage'))
const SingleApproverPage = lazy(() => import('../pages/workFlow/SingleApproverPage'))
const WorkFlowPage = lazy(() => import('../pages/workFlow/WorkFlowPage'))
const RequestLogs = lazy(() => import('../pages/settings/logs/RequestLogs'))
const LogFiles = lazy(() => import('../pages/settings/logs/LogFiles'))
const EmailLogs = lazy(() => import('../pages/settings/logs/EmailLogs'))
const BackUpApprovers = lazy(() => import('../pages/expenseItems/backupApprovers/BackUpApprovers'))
const UserCCFeeds = lazy(() => import('../pages/settings/automation/UserCCFeeds'))
const UserCCFeedsReconciliation = lazy(() => import('../pages/settings/automation/UserCCFeedsReconciliation'))
const MergeLineItems = lazy(() => import('../pages/settings/mergeLineItems/MergeLineItems'))
const ProfilePage = lazy(() => import('../modules/profile/ProfilePage'))
const UserProfilePage = lazy(() => import('../modules/profile/UserProfilePage'))
const WizardsPage = lazy(() => import('../modules/wizards/WizardsPage'))
const AccountPage = lazy(() => import('../modules/accounts/AccountPage'))
const WidgetsPage = lazy(() => import('../modules/widgets/WidgetsPage'))
const ChatPage = lazy(() => import('../modules/apps/chat/ChatPage'))
const UsersPage = lazy(() => import('../modules/apps/user-management/UsersPage'))
const AnomalyUserDetailPage = lazy(() => import('../pages/expenseAnomalies/AnomalyUserDetailPage'))
const AnomalyLineItemsPage = lazy(() => import('../pages/expenseAnomalies/AnomalyLineItemsPage'))

const Lazy = ({ children }: WithChildren) => <SuspensedView>{children}</SuspensedView>

const PrivateRoutes = () => {
  const {
    roleID,
    reportPermision,
    isOrgUserImpersonate,
    isExpenseAnomaliesModuleEnabled,
    isExpenseAnomaliesEnabledForManager,
  } = useAppSelector(selectAuth)

  const canAccessExpenseAnomalies =
    isExpenseAnomaliesModuleEnabled !== false &&
    (roleID === constraintConfig.roleID.role2 ||
      roleID === constraintConfig.roleID.roleIDAdmin ||
      (roleID === constraintConfig.roleID.role4 && isExpenseAnomaliesEnabledForManager === true))

  return (
    <Routes>
      <Route element={<MasterLayout />}>
        {roleID === constraintConfig.roleID.role10 ?
          <Route path='auth/*' element={<Navigate to='/ap-approved' />} /> :
          roleID === constraintConfig.roleID.roleIDPortalSupport ?
            <Route path='auth/*' element={<Navigate to='/portal-support-requests' />} /> :
            roleID === constraintConfig.roleID.roleExecutive ?
              <Route path='auth/*' element={<Navigate to='/approval-queue' />} />
              : <Route path='auth/*' element={<Navigate to='/dashboard' />} />
        }
        {roleID !== constraintConfig.roleID.role10 && roleID !== constraintConfig.roleID.roleIDPortalSupport &&
          <Route path='dashboard' element={<DashboardWrapper />} />}

        {(roleID === constraintConfig.roleID.role4 || roleID === constraintConfig.roleID.role5 || roleID === constraintConfig.roleID.roleIDAdmin) && (
          <>
            <Route path={allRoutes.expenseWizard} element={<ExpenseSubmissionWrapper />} />
            <Route path={allRoutes.expenseReports} element={<ExpenseItemWrapper />} />
            <Route path={`${allRoutes.expenseReports}/pending`} element={<ExpenseItemWrapper />} />
            <Route path={`${allRoutes.expenseReports}/approved`} element={<ExpenseItemWrapper />} />
            <Route path={`${allRoutes.expenseReports}/apApproved`} element={<ExpenseItemWrapper />} />
            <Route path='expense-line-items' element={<DraftItemsPage />} />
            <Route path='report/:id' element={<Lazy><Report /></Lazy>} />
            <Route path='receipt-vault' element={<InvoicesWrapper />} />
            <Route path='/allreceipts' element={<AllInvoicesAdmin />} />
          </>
        )}
        {roleID === constraintConfig.roleID.role2 || roleID === constraintConfig.roleID.roleIDAdmin || roleID === constraintConfig.roleID.role4 || roleID === constraintConfig.roleID.role5 || roleID === constraintConfig.roleID.roleIDAdmin ?
          <Route path='card' element={<CardWrapper />} />
          : null
        }
        {roleID === constraintConfig.roleID.role2 || roleID === constraintConfig.roleID.roleIDAdmin || reportPermision === true ?
          <Route path={allRoutes.searchExpense} element={<ExpenseSearch />} />
          : null}
        {(roleID === constraintConfig.roleID.role2 || roleID === constraintConfig.roleID.roleIDAdmin || roleID === constraintConfig.roleID.role4 || roleID === constraintConfig.roleID.roleExecutive) && (
          <>
            <Route path='approval-queue' element={<TeamMemberExpenses />} />
            <Route path='approval-queue/all' element={<TeamMemberExpenses />} />
            <Route path='approval-queue/pending' element={<TeamMemberExpenses />} />
          </>
        )}
        {(roleID === constraintConfig.roleID.role2 || roleID === constraintConfig.roleID.role4 || roleID === constraintConfig.roleID.roleExecutive) && (
          <>
            <Route path='budget-allocation/budget-history' element={<BudgetHistory />} />
            <Route path='budget-allocation/budget' element={<BudgetPage />} />
            <Route path='budget-allocation/category-budgeting' element={<CategoryBudgetWrapper />} />
          </>
        )}
        <Route path='/budget-history' element={<BudgetHistory />} />
        <Route path='team-management' element={<ReporteesPage />} />
        <Route path='budget-allocation/budgets' element={<BudgetPage />} />
        {(roleID === constraintConfig.roleID.role4) && (
          <>
            <Route path='active/team-management' element={<ReporteesPage />} />
            <Route path='all/team-management' element={<ReporteesPage />} />
          </>
        )}

        {roleID !== constraintConfig.roleID.role10 && roleID !== constraintConfig.roleID.role1 &&
          <Route path={`${allRoutes.analytics}/my-report/*`} element={<MyReportPage />} />
        }
        {roleID === constraintConfig.roleID.role4 ?
          <Route path={`${allRoutes.analytics}/team-report/*`} element={<TeamReportPage />} />
          : null}
        {(roleID === constraintConfig.roleID.role4 || roleID === constraintConfig.roleID.role10 || roleID === constraintConfig.roleID.role2 || roleID === constraintConfig.roleID.roleIDAdmin) && (
          <Route path={`${allRoutes.analytics}/org-report/*`} element={<QueryReportPage />} />
        )}

        {canAccessExpenseAnomalies && (
          <>
            <Route path={`${allRoutes.analytics}/anomalies/users/:userId/line-items`} element={<Lazy><AnomalyLineItemsPage /></Lazy>} />
            <Route path={`${allRoutes.analytics}/anomalies/users/:userId`} element={<Lazy><AnomalyUserDetailPage /></Lazy>} />
            <Route path={`${allRoutes.analytics}/anomalies`} element={<AnomaliesDetectionPage />} />
          </>
        )}

        {(roleID === constraintConfig.roleID.role1) && (
          <>
            <Route path='organization' element={<Lazy><OrganizationPageWrapper /></Lazy>} />
            <Route path='organization/active' element={<Lazy><OrganizationPageWrapper /></Lazy>} />
            <Route path='organization/new' element={<Lazy><OrganizationPageWrapper /></Lazy>} />
            <Route path='organization/inprogress' element={<Lazy><OrganizationPageWrapper /></Lazy>} />
            <Route path='roles' element={<Lazy><RolesPageWrapper /></Lazy>} />
            <Route path='settings/survey-campaigns' element={<Lazy><SurveyCampaignsPage /></Lazy>} />
            <Route path='settings/survey-dashboard' element={<Lazy><SurveyDashboardPage /></Lazy>} />
            <Route path='settings/user-notifications' element={<Lazy><UserNotificationsAdminPage /></Lazy>} />
          </>
        )}
        {(roleID === constraintConfig.roleID.role1 || roleID === constraintConfig.roleID.role2 || roleID === constraintConfig.roleID.roleIDAdmin || roleID === constraintConfig.roleID.roleExecutive) && (
          <>
            <Route path='expense-users' element={<UsersPageWrapper />} />
            <Route path='all/expense-users' element={<UsersPageWrapper />} />
            <Route path='active/expense-users' element={<UsersPageWrapper />} />
            <Route path='expense-users/managers' element={<UsersPageWrapper />} />
            <Route path='active/managers' element={<UsersPageWrapper />} />
          </>
        )}

        {isOrgUserImpersonate == true && (
          <Route path='impersonate-user' element={<ImpersonateUserPage />} />
        )}
        <Route path='impersonate-user-debug' element={<ImpersonateUserPage />} />
        {(roleID === constraintConfig.roleID.role2 ||
          roleID === constraintConfig.roleID.roleIDAdmin ||
          roleID === constraintConfig.roleID.role1 ||
          roleID === constraintConfig.roleID.roleIDPortalSupport) && (
          <>
            <Route path='receipt-vault' element={<InvoicesWrapper />} />
            <Route path='/allreceipts' element={<AllInvoicesAdmin />} />
          </>
        )}
        {(roleID === constraintConfig.roleID.role2 || roleID === constraintConfig.roleID.roleIDAdmin) && (
          <>
            <Route path='settings/org-settings' element={<Lazy><OrgSettings /></Lazy>} />
            <Route path='settings/vendor' element={<Lazy><VendorWrapper /></Lazy>} />
            <Route path='settings/workflow' element={<Lazy><WorkFlowPage /></Lazy>} />
            <Route path='settings/mileage' element={<Lazy><MileageWrapper /></Lazy>} />
            <Route path='settings/categories' element={<Lazy><Categories /></Lazy>} />
            <Route path='settings/departments' element={<Lazy><DepartmentWrapper /></Lazy>} />
            <Route path='settings/designations' element={<Lazy><DesignationWrapper /></Lazy>} />
            <Route path='settings/countries' element={<Lazy><CountriesWrapper /></Lazy>} />
            <Route path='settings/division' element={<Lazy><DivisionWrapper /></Lazy>} />
            <Route path='settings/expense-violation' element={<Lazy><ExpenseVoilationWrapper /></Lazy>} />
            <Route path='settings/locations' element={<Lazy><LocationWrapper /></Lazy>} />
            <Route path={`settings/automation${allRoutes.bankCCFeedFiles}`} element={<Lazy><BankCCFeedFiles /></Lazy>} />
            <Route path={`settings/automation${allRoutes.bankCCFeedMissingFiles}`} element={<Lazy><BankCCFeedMissingFiles /></Lazy>} />
            <Route path={`settings/automation${allRoutes.SSToBCSyncFeeds}`} element={<Lazy><SSToBCSyncFeeds /></Lazy>} />
            <Route path={`settings/automation${allRoutes.bankCCFeeds}`} element={<Lazy><BankCCFeeds /></Lazy>} />
            <Route path={`settings/automation${allRoutes.bankCCFeeds}`} element={<Lazy><AutomationCCFeedsStatsEnhancements /></Lazy>} />
            <Route path={`settings/automation${allRoutes.userCCFeeds}`} element={<Lazy><UserCCFeeds /></Lazy>} />
            <Route path={`settings/automation${allRoutes.userCCFeedsReconciliation}`} element={<Lazy><UserCCFeedsReconciliation /></Lazy>} />
            <Route path={`settings/automation${allRoutes.mergeLineItems}`} element={<Lazy><MergeLineItems /></Lazy>} />
            <Route path={`settings/automation${allRoutes.automationCCFeedsStatsEnhancements}`} element={<Lazy><AutomationCCFeedsStatsEnhancements /></Lazy>} />
            <Route path={`settings${allRoutes.requestLogs}`} element={<Lazy><RequestLogs /></Lazy>} />
            <Route path={`settings${allRoutes.logFiles}`} element={<Lazy><LogFiles /></Lazy>} />
            <Route path={`settings${allRoutes.emailLogs}`} element={<Lazy><EmailLogs /></Lazy>} />
            <Route path={`settings${allRoutes.backupApprovers}`} element={<Lazy><BackUpApprovers /></Lazy>} />
            <Route path='settings/survey-campaigns' element={<Lazy><SurveyCampaignsPage /></Lazy>} />
            <Route path='settings/survey-dashboard' element={<Lazy><SurveyDashboardPage /></Lazy>} />
            <Route path='settings/user-notifications' element={<Lazy><UserNotificationsAdminPage /></Lazy>} />
            <Route path='settings/org-settings/*' element={<Lazy><OrgSettings /></Lazy>} />
          </>
        )}
        {(roleID === constraintConfig.roleID.role2 || roleID === constraintConfig.roleID.roleIDAdmin || roleID === constraintConfig.roleID.roleIDPortalSupport) && (
          <Route path='portal-support-requests' element={<HelpSupportPage />} />
        )}
        <Route path='ap-approved' element={<ExpenseForAp />} />
        <Route path='approvers' element={<Lazy><ApproversPage /></Lazy>} />
        <Route path='approvers/:expenseId/:dateTime' element={<Lazy><SingleApproverPage /></Lazy>} />
        <Route path='expense-item-detail/:expenseId/:dateTime' element={<Lazy><ExpenseItemDetail /></Lazy>} />
        <Route path='clients' element={<Lazy><ClientWrapper /></Lazy>} />
        <Route path='projects' element={<Lazy><ProjectsWrapper /></Lazy>} />
        <Route path='map' element={<Lazy><GoogleMapComponent /></Lazy>} />
        <Route path='builder' element={<Lazy><BuilderPageWrapper /></Lazy>} />
        <Route path='menu-test' element={<Lazy><MenuTestPage /></Lazy>} />

        <Route path='profile/*' element={<Lazy><ProfilePage /></Lazy>} />
        <Route path='user-profile/*' element={<Lazy><UserProfilePage /></Lazy>} />
        <Route path='crafted/pages/wizards/*' element={<Lazy><WizardsPage /></Lazy>} />
        <Route path='crafted/widgets/*' element={<Lazy><WidgetsPage /></Lazy>} />
        <Route path='/account/*' element={<Lazy><AccountPage /></Lazy>} />
        <Route path='apps/chat/*' element={<Lazy><ChatPage /></Lazy>} />
        <Route path='apps/user-management/*' element={<Lazy><UsersPage /></Lazy>} />
        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
    </Routes>
  )
}

/**
 * Fallback for remaining lazy routes only.
 * Renders as an in-content card under the toolbar — never a top-header progress bar.
 */
const SuspensedView: FC<WithChildren> = ({ children }) => {
  return (
    <Suspense
      fallback={
        <div className='card ss-route-loading-card'>
          <div className='card-body'>
            <LoadingComponent label='Loading page...' className='py-10' />
          </div>
        </div>
      }
    >
      {children}
    </Suspense>
  )
}

export { PrivateRoutes }
